import { Box, Button } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { Link, useNavigate } from "react-router-dom";
import StyledTextField from "../components/styled-text-field.compoment";
import config from "../config";
import logo from "../images/logo-with-caption.webp";
import { _post } from "../services/api.service";
import { _notify, NotificationType } from "../services/notification.service";
import { COLORS } from "../theme/theme.config";
import { getRecaptchaToken } from "../utils/recaptcha";

const RESEND_COOLDOWN_SECONDS = 60;

const ActivateAccountForm = ({
  executeRecaptcha,
}: {
  executeRecaptcha?: (action?: string) => Promise<string>;
}) => {
  const navigate = useNavigate();
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN_SECONDS);
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const email = sessionStorage.getItem("USER_EMAIL") || "";

  useEffect(() => {
    if (cooldown <= 0) {
      return;
    }

    const timer = window.setInterval(() => {
      setCooldown((current) => current - 1);
    }, 1000);

    return () => window.clearInterval(timer);
  }, [cooldown]);

  const handleChange = (value: string, index: number) => {
    if (!/^\d?$/.test(value)) return;
    const updated = [...otp];
    updated[index] = value;
    setOtp(updated);
    if (value && index < 5) inputRefs.current[index + 1]?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const paste = e.clipboardData.getData("text").trim();
    if (!/^\d{6}$/.test(paste)) return;
    const arr = paste.split("");
    setOtp(arr);
    arr.forEach((digit, i) => {
      if (inputRefs.current[i]) inputRefs.current[i]!.value = digit;
    });
    inputRefs.current[5]?.focus();
  };

  const handleResendCode = async () => {
    if (!email || resending || cooldown > 0) {
      return;
    }

    const token = await getRecaptchaToken(executeRecaptcha, "resend_verify_email");
    if (!token) {
      return;
    }

    setResending(true);
    const result = await _post(
      "users/send-verify-email/",
      { email, recaptcha: token },
      {},
      false,
      true
    );
    setResending(false);

    if (result) {
      _notify(NotificationType.SUCCESS, "A new activation code has been sent to your email.");
      setCooldown(RESEND_COOLDOWN_SECONDS);
      setOtp(["", "", "", "", "", ""]);
      inputRefs.current[0]?.focus();
    }
  };

  const handleCodeSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!email) {
      _notify(NotificationType.ERROR, "Email not found. Please sign up again.");
      return;
    }

    const fullCode = otp.join("");
    const payload = { email, code: fullCode };
    const userData = await _post("users/verify-email/", payload);
    if (userData) {
      sessionStorage.setItem("USER_EMAIL", userData.email);
      localStorage.setItem("TOKEN", userData.access);
      localStorage.setItem("REFRESH_TOKEN", userData.refresh);
      navigate("/");
    }
  };

  return (
    <div className="outer">
      <div className="middle">
        <div className="login_form">
          <form onSubmit={handleCodeSubmit} autoComplete="off">
            <div className="mb-4">
              <img src={logo} alt="Logo" style={{ height: "40px" }} />
            </div>

            <Box display="flex" justifyContent="center" gap={2} mb={3}>
              {otp.map((digit, index) => (
                <StyledTextField
                  key={index}
                  inputRef={(el) => (inputRefs.current[index] = el)}
                  value={digit}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleChange(e.target.value, index)
                  }
                  onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) =>
                    handleKeyDown(e, index)
                  }
                  onPaste={handlePaste}
                  inputProps={{
                    maxLength: 1,
                    style: {
                      textAlign: "center",
                      fontSize: "22px",
                      width: "45px",
                      height: "45px",
                      padding: 0,
                    },
                  }}
                />
              ))}
            </Box>

            <p
              style={{
                color: "#808080",
                lineHeight: "normal",
                fontStyle: "italic",
                fontSize: "13px",
                marginBottom: "8px",
              }}
            >
              {email
                ? `A 6-digit activation code has been sent to ${email}.`
                : "A 6-digit activation code has been sent to your email."}
            </p>

            {email ? (
              <Button
                type="button"
                variant="text"
                onClick={handleResendCode}
                disabled={resending || cooldown > 0}
                sx={{
                  color: COLORS.green,
                  textTransform: "none",
                  fontSize: "13px",
                  fontWeight: "bold",
                  p: 0,
                  minWidth: 0,
                  mb: 2,
                  "&:disabled": { color: "#555" },
                }}
              >
                {resending
                  ? "Sending..."
                  : cooldown > 0
                    ? `Resend code in ${cooldown}s`
                    : "Resend code"}
              </Button>
            ) : (
              <p className="sign_up" style={{ marginBottom: "16px" }}>
                Missing email?
                <Link
                  style={{
                    marginLeft: "7px",
                    color: COLORS.green,
                    fontWeight: "bold",
                    fontSize: "13px",
                    textDecoration: "none",
                  }}
                  to="/verify-code"
                >
                  Request a new code
                </Link>
              </p>
            )}

            <Button
              sx={{
                backgroundColor: COLORS.green,
                padding: "14px",
                borderRadius: "0",
                color: "#010624",
                "&:hover": { backgroundColor: COLORS.greenHover },
                "&:disabled": { backgroundColor: COLORS.greenDisabled, color: "#010624" },
              }}
              disabled={otp.join("").length !== 6 || !email}
              fullWidth
              type="submit"
            >
              Activate Account
            </Button>

            <p className="sign_up mt-4">
              Back to
              <Link
                style={{
                  marginLeft: "7px",
                  color: COLORS.green,
                  fontWeight: "bold",
                  fontSize: "13px",
                  textDecoration: "none",
                }}
                to="/login"
              >
                Login
              </Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

const ActivateAccountWithRecaptcha = () => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  return <ActivateAccountForm executeRecaptcha={executeRecaptcha} />;
};

const ActivateAccount = () => {
  if (process.env.REACT_APP_VERIFY_RECAPTCHA === "false") {
    return <ActivateAccountForm />;
  }

  return (
    <GoogleReCaptchaProvider reCaptchaKey={config.captchaKey}>
      <ActivateAccountWithRecaptcha />
    </GoogleReCaptchaProvider>
  );
};

export default ActivateAccount;
